/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The config-driven Real Estate pages (reEntityConfig) in the tenant's
 * country. One pass over a page config:
 *
 *   - fields, detail fields, columns and filters whose column does not apply
 *     in this country are dropped (pack.hidden_fields, keyed by the page's
 *     table, which is its permissionObject);
 *   - outside the UAE, the country's choices and words replace the UAE ones:
 *     regions (State), rent frequencies, portals, the tax name, the currency
 *     default and "(AED)" in labels.
 *
 * For a UAE tenant only the first applies, and it only removes the columns
 * added for Australia, so every UAE page reads as it did before variants.
 */

import { choiceOptions, isHidden } from "./variant";

const nameOf = (item) => item?.name || item?.field || item?.accessor || null;

function relabel(text, pack) {
  if (typeof text !== "string") return text;
  return text.replace(/\(AED\)/g, `(${pack.currency})`).replace(/\bAED\b/g, pack.currency);
}

function localiseField(field, pack) {
  if (pack.code === "UAE" || !field) return field;
  const out = { ...field, label: relabel(field.label, pack) };
  switch (field.name) {
    case "state":
      return { ...out, label: pack.region_label, type: "select", options: choiceOptions(pack.regions) };
    case "currency":
      return { ...out, defaultValue: pack.currency };
    case "city":
      return { ...out, defaultValue: undefined };
    case "rent_frequency":
      return { ...out, options: choiceOptions(pack.rent_frequencies) };
    case "portals":
      return {
        ...out,
        helperText: `Comma separated: ${(pack.listing_portals || []).map((p) => p.code).join(", ")}`,
      };
    case "is_vat_registered":
      return { ...out, label: `${pack.tax_name} registered` };
    default:
      return out;
  }
}

function localiseColumn(column, pack) {
  if (pack.code === "UAE") return column;
  return { ...column, Header: relabel(column.Header, pack) };
}

function keepFields(list, table, pack) {
  if (!Array.isArray(list)) return list;
  return list
    .filter((item) => !isHidden(table, nameOf(item), pack))
    .map((item) => localiseField(item, pack));
}

/** The page config for this country. Pure; `pack` from variant.js. */
export function localiseConfig(config, pack) {
  if (!config || !pack) return config;
  const table = config.permissionObject;
  const out = { ...config };
  if (config.createForm?.fields) {
    out.createForm = { ...config.createForm, fields: keepFields(config.createForm.fields, table, pack) };
  }
  if (config.editFields) out.editFields = keepFields(config.editFields, table, pack);
  if (config.detailFields) out.detailFields = keepFields(config.detailFields, table, pack);
  if (Array.isArray(config.filters)) {
    out.filters = config.filters.filter((f) => !isHidden(table, f.param, pack));
  }
  if (typeof config.columns === "function") {
    const columns = config.columns;
    out.columns = (...args) =>
      columns(...args)
        .filter((c) => !isHidden(table, nameOf(c), pack))
        .map((c) => localiseColumn(c, pack));
  }
  return out;
}
